import React from 'react';
import { Bus, NoBus } from '@lt/components/icons-react/userflow';
import {
  Insurance,
  NoInsurance,
  QuestionCircle,
} from '@lt/components/icons-react';
import styled, { css } from 'styled-components';
import { Bubble } from '@lt/components';
import { useTranslation } from '@lt/localization';
import PopupTransfer from './PopupTransfer';
import PopupInsurance from './PopupInsurance';

interface Props {
  sidebar: boolean;
  insurance: boolean;
  transfer: boolean;
  transferPrivate: boolean;
  airports?: {
    id: number;
    name: string;
    transferTime: number;
  };
  isMobile?: boolean;
}

const iconSize = {
  width: 16,
  height: 16,
};

export const TransferMedInsuranceInfo = ({
  sidebar,
  insurance,
  transfer,
  transferPrivate,
  airports,
  isMobile = false,
}: Props) => {
  const { t } = useTranslation();
  return (
    <Wrapper $sidebar={sidebar}>
      {!sidebar ? <Title>{t('checkout.flight.includedWith')}</Title> : null}
      <div>
        <Row>
          {transfer ? <BusIcon {...iconSize} /> : <NoBusIcon {...iconSize} />}
          <Info $included={transfer}>
            <div>
              {transfer
                ? t('checkout.packageInfo.transfer.included')
                : t('checkout.packageInfo.transfer.not_included')}
            </div>
          </Info>
          {!sidebar && (
            <Bubble
              renderExtras={() =>
                PopupTransfer({
                  transfer,
                  airports,
                  transferPrivate,
                  isMobile,
                  t,
                })
              }
              text=" "
              width="min"
            >
              <StyledQuestionCircle />
            </Bubble>
          )}
        </Row>
        <Row>
          {insurance ? (
            <InsuranceIcon {...iconSize} />
          ) : (
            <NoInsuranceIcon {...iconSize} />
          )}
          <Info $included={insurance}>
            <div>
              {insurance
                ? t('checkout.packageInfo.medInsurance.included')
                : t('checkout.packageInfo.medInsurance.not_included')}
            </div>
          </Info>
          {!sidebar && (
            <Bubble
              renderExtras={() => PopupInsurance({ insurance, t })}
              text=" "
            >
              <StyledQuestionCircle />
            </Bubble>
          )}
        </Row>
      </div>
    </Wrapper>
  );
};

const StyledQuestionCircle = styled(QuestionCircle).attrs({
  width: '14px',
  height: '14px',
})`
  stroke: #919499;
  cursor: pointer;

  > path {
    stroke-width: 0.5;
    fill: #919499;
  }
`;

const Wrapper = styled.div<{ $sidebar: boolean }>`
  margin: ${({ $sidebar }) => $sidebar && '0px 8px 8px'};
`;

const sharedIconStyles = css`
  flex-shrink: 0;
`;

const BusIcon = styled(Bus)`
  ${sharedIconStyles}
`;
const NoBusIcon = styled(NoBus)`
  ${sharedIconStyles}
`;
const InsuranceIcon = styled(Insurance)`
  ${sharedIconStyles}
`;
const NoInsuranceIcon = styled(NoInsurance)`
  ${sharedIconStyles}
`;

const Title = styled.div`
  font-weight: 700;
  color: #17181a;
  margin-bottom: 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const Info = styled.div<{ $included?: boolean }>`
  display: flex;
  justify-content: space-between;

  margin-left: 4px;
  margin-right: 5px;

  ${({ $included }) => !$included && 'color: #D0021B'}
`;
